import { useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  BriefcaseBusiness,
  Plus,
  UserPlus,
  Check,
  HelpCircle,
  X,
  MoreHorizontal,
  Search,
  FileText,
  ExternalLink,
  MapPin,
  Users,
  Calendar,
  Pencil,
} from 'lucide-react';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Drawer } from '@/shared/components/ui/Drawer';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ROUTES } from '@/routes/paths';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  openResume,
  useJobApplications,
  useJobPostings,
  useLinkedOfferLetter,
  useMasterDepartments,
  useRecruitmentAccess,
  useRecruitmentMutations,
} from '../api';
import type { ApplicationStage, EmploymentType, JobApplication, JobPosting, JobStatus } from '../types';

const MAX_RESUME_BYTES = 10 * 1024 * 1024; // keep in sync with server limit

// ─── Constants ──────────────────────────────────────────────────────────────────

const STAGE_LABELS: Record<ApplicationStage, string> = {
  applied: 'Applied',
  ai_screened: 'AI Screened',
  contacted: 'Contacted',
  interview: 'Interview',
  hired: 'Hired',
  rejected: 'Rejected',
};

const STAGE_TONES: Record<ApplicationStage, BadgeTone> = {
  applied: 'info',
  ai_screened: 'warning',
  contacted: 'primary',
  interview: 'info',
  hired: 'success',
  rejected: 'danger',
};

const STAGE_ORDER: ApplicationStage[] = ['applied', 'ai_screened', 'contacted', 'interview', 'hired', 'rejected'];

const OFFER_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft — needs salary details',
  sent: 'Sent to candidate',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const OFFER_STATUS_TONES: Record<string, BadgeTone> = {
  draft: 'neutral',
  sent: 'info',
  accepted: 'success',
  rejected: 'danger',
  withdrawn: 'warning',
};

const NEXT_STAGE: Partial<Record<ApplicationStage, ApplicationStage>> = {
  applied: 'ai_screened',
  ai_screened: 'contacted',
  contacted: 'interview',
  interview: 'hired',
};

const EMPLOYMENT_OPTIONS: { label: string; value: EmploymentType }[] = [
  { label: 'Full Time', value: 'full_time' },
  { label: 'Part Time', value: 'part_time' },
  { label: 'Contract', value: 'contract' },
  { label: 'Intern', value: 'intern' },
];

const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  full_time: 'FULL TIME',
  part_time: 'PART TIME',
  contract: 'CONTRACT',
  intern: 'INTERN',
};

// ─── Helpers ────────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function formatJobDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

// ─── Main Page Component ────────────────────────────────────────────────────────

export function RecruitmentPage() {
  const { data: jobs, isLoading: jobsLoading } = useJobPostings();
  const { createJob, updateJob, addApplication, moveStage, screenApplication } = useRecruitmentMutations();

  // Top level tab: 'jobs' (Job Postings) | 'applications' (Applications)
  const [activeMainTab, setActiveMainTab] = useState<'jobs' | 'applications'>('jobs');

  // Job Postings tab filters & search
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [jobStatusFilter, setJobStatusFilter] = useState<'all' | JobStatus>('all');
  const [editingJob, setEditingJob] = useState<JobPosting | null>(null);

  // Applications tab filters & search
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState<ApplicationStage | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name'>('newest');

  // Modals & Drawers
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showNewCandidateModal, setShowNewCandidateModal] = useState(false);
  const [drawerApp, setDrawerApp] = useState<JobApplication | null>(null);
  const [rejectionTarget, setRejectionTarget] = useState<JobApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Default to all jobs when no specific job selected
  const activeJobId = selectedJobId ?? '';
  const { data: applications, isLoading: appsLoading } = useJobApplications(activeJobId || null);

  const activeJob = useMemo(
    () => (activeJobId ? jobs?.find((j) => j._id === activeJobId) : null) ?? null,
    [jobs, activeJobId]
  );

  // Access rights
  const { data: access } = useRecruitmentAccess();
  const canManage = access?.canManageCandidates === true;
  const canCreateJob = access?.canManageJobs === true;

  // Applications count mapped by Job ID
  const applicationsCountByJob = useMemo(() => {
    const map: Record<string, number> = {};
    for (const app of applications ?? []) {
      map[app.jobId] = (map[app.jobId] ?? 0) + 1;
    }
    return map;
  }, [applications]);

  // Filtered Job Postings
  const filteredJobs = useMemo(() => {
    let list = jobs ?? [];
    if (jobStatusFilter !== 'all') {
      list = list.filter((j) => j.status === jobStatusFilter);
    }
    if (jobSearchTerm.trim()) {
      const term = jobSearchTerm.toLowerCase();
      list = list.filter(
        (j) =>
          j.title.toLowerCase().includes(term) ||
          j.department.toLowerCase().includes(term) ||
          (j.location || '').toLowerCase().includes(term)
      );
    }
    return list;
  }, [jobs, jobStatusFilter, jobSearchTerm]);

  // Filtered Applications
  const filteredApps = useMemo(() => {
    let list = applications ?? [];
    if (stageFilter !== 'all') {
      list = list.filter((a) => a.stage === stageFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (a) => a.candidateName.toLowerCase().includes(term) || a.email.toLowerCase().includes(term)
      );
    }
    list = [...list].sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortBy === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return a.candidateName.localeCompare(b.candidateName);
    });
    return list;
  }, [applications, stageFilter, searchTerm, sortBy]);

  // Stage counts for application pipeline tabs
  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    for (const stage of STAGE_ORDER) counts[stage] = 0;
    for (const app of applications ?? []) {
      counts[app.stage] = (counts[app.stage] ?? 0) + 1;
      counts.all += 1;
    }
    return counts;
  }, [applications]);

  // ─── Actions ─────────────────────────────────────────────────────────────────

  const handleToggleJobStatus = (job: JobPosting) => {
    const nextStatus: JobStatus = job.status === 'open' ? 'draft' : 'open';
    updateJob.mutate(
      { id: job._id, status: nextStatus },
      {
        onSuccess: () => {
          toast.success(`Job "${job.title}" marked as ${nextStatus.toUpperCase()}`);
        },
        onError: (err) => {
          toast.error(getApiErrorMessage(err, 'Failed to update job status'));
        },
      }
    );
  };

  const handleAdvance = (app: JobApplication) => {
    const next = NEXT_STAGE[app.stage];
    if (!next) return;
    moveStage.mutate(
      { id: app._id, stage: next },
      {
        onSuccess: (result) => {
          if (next === 'hired') {
            toast.success(
              `Moved ${app.candidateName} to Hired — a draft offer letter has been created. Complete it on the Offer Letters page.`,
              { duration: 6000 }
            );
            if (result?.onboardingLink) toast.success(`Onboarding link: ${result.onboardingLink}`, { duration: 8000 });
          } else {
            toast.success(`Moved ${app.candidateName} to ${STAGE_LABELS[next]}`);
          }
        },
      }
    );
  };

  const handleReject = (app: JobApplication) => {
    setRejectionTarget(app);
    setRejectionReason('');
  };

  const confirmReject = () => {
    if (!rejectionTarget) return;
    moveStage.mutate(
      { id: rejectionTarget._id, stage: 'rejected', rejectionReason },
      {
        onSuccess: () => {
          toast.success(`${rejectionTarget.candidateName} moved to Rejected`);
          setRejectionTarget(null);
        },
      }
    );
  };

  // ─── Loading state ──────────────────────────────────────────────────────────

  if (jobsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-5 bg-surface-container-lowest/40 min-h-screen">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-on-surface tracking-tight">Recruitment</h1>

        <div className="flex items-center gap-2.5">
          {activeMainTab === 'applications' && canManage && (
            <button
              type="button"
              onClick={() => setShowNewCandidateModal(true)}
              className="flex items-center gap-1.5 rounded-lg border border-outline bg-surface px-3.5 py-2 text-xs font-semibold text-on-surface shadow-xs hover:bg-surface-variant transition-colors"
            >
              <UserPlus className="h-4 w-4 text-on-surface-variant" />
              <span>Add Candidate</span>
            </button>
          )}

          {canCreateJob && (
            <button
              type="button"
              onClick={() => setShowNewJobModal(true)}
              className="flex items-center gap-1.5 rounded-lg bg-[#5B4DFB] hover:bg-[#4d3ef7] px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>New Job Posting</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Navigation Tabs: Job Postings & Applications */}
      <div className="flex border-b border-outline/60">
        <button
          type="button"
          onClick={() => setActiveMainTab('jobs')}
          className={`relative pb-3 pt-1 text-sm font-semibold transition-colors ${
            activeMainTab === 'jobs' ? 'text-primary' : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          Job Postings
          {activeMainTab === 'jobs' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
        </button>
        <button
          type="button"
          onClick={() => setActiveMainTab('applications')}
          className={`relative ml-8 pb-3 pt-1 text-sm font-semibold transition-colors ${
            activeMainTab === 'applications' ? 'text-primary' : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          Applications
          {activeMainTab === 'applications' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────────────────── */}
      {/* TAB 1: JOB POSTINGS */}
      {/* ─────────────────────────────────────────────────────────────────────────── */}
      {activeMainTab === 'jobs' && (
        <div className="flex flex-col gap-4">
          {/* Search & Status Filter Bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                placeholder="Search by title, department or location..."
                value={jobSearchTerm}
                onChange={(e) => setJobSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4 text-on-surface-variant" />}
              />
            </div>
            <div className="w-52 shrink-0">
              <Select
                options={[
                  { label: 'All statuses', value: 'all' },
                  { label: 'Open', value: 'open' },
                  { label: 'Draft', value: 'draft' },
                  { label: 'Closed', value: 'closed' },
                ]}
                value={jobStatusFilter}
                onChange={(e) => setJobStatusFilter(e.target.value as 'all' | JobStatus)}
                aria-label="Filter by status"
              />
            </div>
          </div>

          {/* Job Postings Table Card */}
          <div className="rounded-2xl border border-outline bg-surface shadow-xs overflow-hidden">
            <div className="px-6 pt-5 pb-3">
              <h2 className="text-base font-bold text-on-surface">
                {filteredJobs.length} job posting{filteredJobs.length !== 1 ? 's' : ''}
              </h2>
            </div>

            {filteredJobs.length === 0 ? (
              <div className="py-12">
                <EmptyState
                  icon={<BriefcaseBusiness className="h-6 w-6" />}
                  title="No job postings found"
                  description="Try adjusting your search criteria or create a new job posting."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-outline/50 text-[11px] font-bold tracking-wider uppercase text-on-surface-variant">
                      <th className="px-6 py-3.5">TITLE</th>
                      <th className="px-6 py-3.5">LOCATION</th>
                      <th className="px-6 py-3.5">TYPE</th>
                      <th className="px-6 py-3.5">APPLICATIONS</th>
                      <th className="px-6 py-3.5">OPENINGS</th>
                      <th className="px-6 py-3.5">STATUS</th>
                      <th className="px-6 py-3.5">CREATED</th>
                      <th className="px-6 py-3.5 text-right">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline/40">
                    {filteredJobs.map((job) => {
                      const appCount = applicationsCountByJob[job._id] ?? 0;
                      return (
                        <tr
                          key={job._id}
                          className="hover:bg-surface-container-lowest/60 transition-colors"
                        >
                          {/* Title & Department */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-semibold text-on-surface leading-tight text-sm">
                                {job.title}
                              </span>
                              <span className="mt-0.5 text-xs text-on-surface-variant">
                                {job.department}
                              </span>
                            </div>
                          </td>

                          {/* Location */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5 text-xs text-on-surface-variant">
                              <MapPin className="h-3.5 w-3.5 shrink-0 opacity-70" />
                              <span>{job.location || '—'}</span>
                            </div>
                          </td>

                          {/* Type */}
                          <td className="px-6 py-4">
                            <span className="inline-block rounded-md bg-sky-50 dark:bg-sky-950/40 border border-sky-200/60 dark:border-sky-800/50 px-2.5 py-0.5 text-[10px] font-bold tracking-wider text-sky-600 dark:text-sky-300 uppercase">
                              {EMPLOYMENT_TYPE_LABELS[job.employmentType] || 'FULL TIME'}
                            </span>
                          </td>

                          {/* Applications */}
                          <td className="px-6 py-4">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedJobId(job._id);
                                setActiveMainTab('applications');
                              }}
                              className="flex items-center gap-1.5 text-xs font-semibold text-on-surface hover:text-primary transition-colors"
                              title="View applications for this job"
                            >
                              <Users className="h-3.5 w-3.5 opacity-70" />
                              <span>{appCount}</span>
                            </button>
                          </td>

                          {/* Openings */}
                          <td className="px-6 py-4 text-xs font-medium text-on-surface">
                            {job.openings}
                          </td>

                          {/* Status with Toggle */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2.5">
                              {/* Toggle switch */}
                              <button
                                type="button"
                                role="switch"
                                aria-checked={job.status === 'open'}
                                onClick={() => handleToggleJobStatus(job)}
                                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                  job.status === 'open' ? 'bg-emerald-500' : 'bg-surface-container-high'
                                }`}
                                title={`Toggle status (currently ${job.status})`}
                              >
                                <span
                                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                                    job.status === 'open' ? 'translate-x-4' : 'translate-x-0'
                                  }`}
                                />
                              </button>

                              {/* Status badge pill */}
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                                  job.status === 'open'
                                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200/60'
                                    : job.status === 'draft'
                                    ? 'bg-surface-container-high text-on-surface-variant border border-outline'
                                    : 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200/60'
                                }`}
                              >
                                {job.status}
                              </span>
                            </div>
                          </td>

                          {/* Created Date */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5 text-xs text-on-surface-variant">
                              <Calendar className="h-3.5 w-3.5 shrink-0 opacity-70" />
                              <span>{formatJobDate(job.createdAt)}</span>
                            </div>
                          </td>

                          {/* Actions (Pencil) */}
                          <td className="px-6 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => setEditingJob(job)}
                              className="rounded-md p-1.5 text-on-surface-variant hover:text-on-surface hover:bg-surface-variant transition-colors"
                              title="Edit Job Posting"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────── */}
      {/* TAB 2: APPLICATIONS */}
      {/* ─────────────────────────────────────────────────────────────────────────── */}
      {activeMainTab === 'applications' && (
        <div className="flex flex-col gap-4">
          {/* Pipeline Stage Filter Tabs */}
          <div className="flex gap-1 overflow-x-auto border-b border-outline/60">
            {(['all', ...STAGE_ORDER] as const).map((stage) => {
              const label = stage === 'all' ? 'All applications' : STAGE_LABELS[stage];
              const count = stageCounts[stage] ?? 0;
              const isActive = stageFilter === stage;
              return (
                <button
                  key={stage}
                  onClick={() => setStageFilter(stage)}
                  className={`shrink-0 border-b-2 px-4 py-2 text-xs font-semibold transition-colors ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {label} &middot; {count}
                </button>
              );
            })}
          </div>

          {/* Sort/Filter/Search Bar */}
          <div className="flex items-center gap-3">
            <div className="flex-[3]">
              <Input
                placeholder="Search candidates by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4 text-on-surface-variant" />}
              />
            </div>
            <div className="w-56 shrink-0">
              <Select
                options={[
                  { label: 'All Job Roles', value: '' },
                  ...(jobs ?? []).map((j) => ({ label: j.title, value: j._id })),
                ]}
                value={activeJobId}
                onChange={(e) => setSelectedJobId(e.target.value || null)}
                aria-label="Filter by role"
              />
            </div>
            <div className="w-44 shrink-0">
              <Select
                options={[
                  { label: 'Newest first', value: 'newest' },
                  { label: 'Oldest first', value: 'oldest' },
                  { label: 'Name A-Z', value: 'name' },
                ]}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'name')}
                aria-label="Sort by"
              />
            </div>
          </div>

          {/* Candidates Table Card */}
          <div className="rounded-2xl border border-outline bg-surface shadow-xs overflow-hidden">
            <div className="px-6 pt-5 pb-3">
              <h2 className="text-base font-bold text-on-surface">
                {filteredApps.length} candidate{filteredApps.length !== 1 ? 's' : ''}
              </h2>
            </div>

            {appsLoading ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : filteredApps.length === 0 ? (
              <div className="py-12">
                <EmptyState
                  icon={<BriefcaseBusiness className="h-6 w-6" />}
                  title="No candidates found"
                  description="Try adjusting your filters or add a new candidate."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-outline/50 text-[11px] font-bold tracking-wider uppercase text-on-surface-variant">
                      <th className="w-8 px-6 py-3.5">
                        <input type="checkbox" className="rounded border-outline" aria-label="Select all" />
                      </th>
                      <th className="px-6 py-3.5">Candidates</th>
                      <th className="px-6 py-3.5">Matches to job post</th>
                      <th className="px-6 py-3.5">Rejection Reason</th>
                      <th className="px-6 py-3.5">Activity</th>
                      <th className="px-6 py-3.5">Interest</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline/40">
                    {filteredApps.map((app) => (
                      <ApplicationRow
                        key={app._id}
                        app={app}
                        job={jobs?.find((j) => j._id === app.jobId) ?? null}
                        canManage={canManage}
                        onAdvance={() => handleAdvance(app)}
                        onReject={() => handleReject(app)}
                        onReview={() => setDrawerApp(app)}
                        onRowClick={() => setDrawerApp(app)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────── */}
      {/* MODALS & DRAWERS */}
      {/* ─────────────────────────────────────────────────────────────────────────── */}

      {/* New Job Modal */}
      {showNewJobModal && (
        <NewJobModal
          open={showNewJobModal}
          onClose={() => setShowNewJobModal(false)}
          onCreate={(payload) => {
            createJob.mutate(
              { ...payload, status: 'open' },
              {
                onSuccess: () => {
                  toast.success('Job posting created');
                  setShowNewJobModal(false);
                },
              }
            );
          }}
          loading={createJob.isPending}
        />
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <EditJobModal
          open={!!editingJob}
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onUpdate={({ id, ...payload }) => {
            updateJob.mutate(
              { id, ...payload },
              {
                onSuccess: () => {
                  toast.success('Job posting updated');
                  setEditingJob(null);
                },
                onError: (err) => {
                  toast.error(getApiErrorMessage(err, 'Failed to update job posting'));
                },
              }
            );
          }}
          loading={updateJob.isPending}
        />
      )}

      {/* New Candidate Modal */}
      {showNewCandidateModal && (
        <NewCandidateModal
          open={showNewCandidateModal}
          onClose={() => setShowNewCandidateModal(false)}
          jobs={jobs ?? []}
          defaultJobId={activeJobId || null}
          onAdd={({ jobId, ...payload }) => {
            addApplication.mutate(
              { jobId, ...payload },
              {
                onSuccess: () => {
                  toast.success('Candidate added');
                  setShowNewCandidateModal(false);
                },
                onError: (err) => toast.error(getApiErrorMessage(err, 'Could not add candidate.')),
              }
            );
          }}
          loading={addApplication.isPending}
        />
      )}

      {/* Rejection Reason Modal */}
      <Modal
        open={!!rejectionTarget}
        onClose={() => setRejectionTarget(null)}
        title="Reject Candidate"
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setRejectionTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmReject} loading={moveStage.isPending}>
              Reject
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-on-surface-variant">
          Provide a reason for rejecting <strong>{rejectionTarget?.candidateName}</strong>.
        </p>
        <Input
          label="Rejection reason"
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          placeholder="e.g. Not enough experience"
        />
      </Modal>

      {/* Candidate Detail Drawer */}
      <CandidateDrawer
        app={drawerApp}
        job={drawerApp ? (jobs?.find((j) => j._id === drawerApp.jobId) ?? null) : null}
        open={!!drawerApp}
        onClose={() => setDrawerApp(null)}
        canManage={canManage}
        onAdvance={(a) => handleAdvance(a)}
        onReject={(a) => handleReject(a)}
        onScreen={(a) => {
          screenApplication.mutate(a._id, {
            onSuccess: () => toast.success('AI screening initiated'),
          });
        }}
      />
    </div>
  );
}

// ─── Application Row Component ────────────────────────────────────────────────

interface ApplicationRowProps {
  app: JobApplication;
  job: JobPosting | null;
  canManage: boolean;
  onAdvance: () => void;
  onReject: () => void;
  onReview: () => void;
  onRowClick: () => void;
}

function ApplicationRow({
  app,
  job,
  canManage,
  onAdvance,
  onReject,
  onReview,
  onRowClick,
}: ApplicationRowProps) {
  const matchedSkills = app.screeningMatchedSkills ?? [];
  const requiredSkills = job?.requiredSkills ?? [];

  return (
    <tr
      className="cursor-pointer border-b border-outline/50 transition-colors hover:bg-surface-variant/40"
      onClick={onRowClick}
    >
      <td className="px-6 py-3.5" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" className="rounded border-outline" aria-label={`Select ${app.candidateName}`} />
      </td>
      <td className="px-6 py-3.5">
        <div className="flex flex-col gap-1">
          <span className="font-bold text-on-surface text-sm">{app.candidateName}</span>
          <span className="text-xs text-on-surface-variant">
            {job?.title ?? 'Unknown role'} &middot;
          </span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="rounded-md bg-sky-50 dark:bg-sky-950/40 border border-sky-200/60 dark:border-sky-800/50 px-2 py-0.5 text-[10px] font-bold text-sky-700 dark:text-sky-300 uppercase">
              {STAGE_LABELS[app.stage]?.toUpperCase()}
            </span>
            <span className="text-xs text-on-surface-variant">Applied {timeAgo(app.createdAt)}</span>
          </div>
        </div>
      </td>
      <td className="px-6 py-3.5">
        <div className="flex flex-wrap gap-1">
          {requiredSkills.length === 0 ? (
            <span className="text-xs text-on-surface-variant">—</span>
          ) : (
            requiredSkills.map((skill) => {
              const matched = matchedSkills.includes(skill);
              return (
                <Badge key={skill} tone={matched ? 'success' : 'danger'} className="text-xs">
                  {matched ? '✓' : '✗'} {skill}
                </Badge>
              );
            })
          )}
        </div>
      </td>
      <td className="px-6 py-3.5 max-w-[220px]">
        {app.stage === 'rejected' && app.rejectionReason ? (
          <span className="text-xs text-danger" title={app.rejectionReason}>
            {app.rejectionReason}
          </span>
        ) : (
          <span className="text-xs text-on-surface-variant">—</span>
        )}
      </td>
      <td className="px-6 py-3.5">
        <span className="text-xs text-on-surface-variant">{timeAgo(app.updatedAt)}</span>
      </td>
      <td className="px-6 py-3.5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1.5">
          {canManage && app.stage !== 'hired' && app.stage !== 'rejected' && (
            <button
              type="button"
              onClick={onAdvance}
              className="rounded p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
              title="Advance to next stage"
              aria-label="Advance candidate"
            >
              <Check className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onReview}
            className="rounded p-1 text-on-surface-variant hover:bg-surface-variant"
            title="Review details"
            aria-label="Review candidate"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          {canManage && app.stage !== 'rejected' && (
            <button
              type="button"
              onClick={onReject}
              className="rounded p-1 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
              title="Reject"
              aria-label="Reject candidate"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onReview}
            className="rounded p-1 text-on-surface-variant hover:bg-surface-variant"
            title="More actions"
            aria-label="More actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Edit Job Modal ────────────────────────────────────────────────────────────

interface EditJobModalProps {
  open: boolean;
  job: JobPosting;
  onClose: () => void;
  onUpdate: (payload: { id: string } & Partial<JobPosting>) => void;
  loading: boolean;
}

function EditJobModal({ open, job, onClose, onUpdate, loading }: EditJobModalProps) {
  const [title, setTitle] = useState(job.title);
  const [department, setDepartment] = useState(job.department);
  const [description, setDescription] = useState(job.description || '');
  const [skills, setSkills] = useState((job.requiredSkills || []).join(', '));
  const [employmentType, setEmploymentType] = useState<EmploymentType>(job.employmentType || 'full_time');
  const [location, setLocation] = useState(job.location || '');
  const [openings, setOpenings] = useState(String(job.openings || 1));
  const [status, setStatus] = useState<JobStatus>(job.status || 'open');

  const { data: departments = [] } = useMasterDepartments();

  const handleSubmit = () => {
    if (!title.trim() || !department.trim()) return;
    onUpdate({
      id: job._id,
      title: title.trim(),
      department: department.trim(),
      description: description.trim(),
      requiredSkills: skills
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      employmentType,
      location: location.trim(),
      openings: Math.max(1, parseInt(openings, 10) || 1),
      status,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit Job Posting: ${job.title}`}
      size="lg"
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} disabled={!title.trim() || !department.trim()}>
            Save Changes
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <Input label="Job Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Job Title" />
        <Select
          label="Department"
          options={departments.map((d) => ({ label: d.label, value: d.label }))}
          placeholder="Select a department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
        />
        <div>
          <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Description</label>
          <textarea
            className="h-24 w-full rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the role..."
          />
        </div>
        <Input
          label="Required Skills (comma separated)"
          value={skills}
          onChange={(e) => setSkills(e.target.value)}
          placeholder="React, TypeScript, Node.js"
        />
        <div className="grid grid-cols-3 gap-4">
          <Select
            label="Employment Type"
            options={EMPLOYMENT_OPTIONS}
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}
          />
          <Input label="Location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Pune" />
          <Select
            label="Status"
            options={[
              { label: 'Open', value: 'open' },
              { label: 'Draft', value: 'draft' },
              { label: 'Closed', value: 'closed' },
            ]}
            value={status}
            onChange={(e) => setStatus(e.target.value as JobStatus)}
          />
        </div>
        <Input
          label="Openings"
          type="number"
          value={openings}
          onChange={(e) => setOpenings(e.target.value)}
          placeholder="1"
        />
      </div>
    </Modal>
  );
}

// ─── Candidate Drawer Component ───────────────────────────────────────────────

interface CandidateDrawerProps {
  app: JobApplication | null;
  job: JobPosting | null;
  open: boolean;
  onClose: () => void;
  canManage: boolean;
  onAdvance: (app: JobApplication) => void;
  onReject: (app: JobApplication) => void;
  onScreen: (app: JobApplication) => void;
}

function CandidateDrawer({
  app,
  job,
  open,
  onClose,
  canManage,
  onAdvance,
  onReject,
  onScreen,
}: CandidateDrawerProps) {
  const navigate = useNavigate();
  const hasReachedOffer = !!app && app.stage === 'hired';
  const { data: linkedOffer } = useLinkedOfferLetter(hasReachedOffer ? app?._id ?? null : null);

  if (!app) return null;

  return (
    <Drawer open={open} onClose={onClose} width="w-[480px]" title={app.candidateName}>
      <div className="flex flex-col gap-6">
        {/* Contact Info */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-on-surface">Contact Information</h3>
          <div className="space-y-1 text-sm text-on-surface-variant">
            <p>Email: {app.email}</p>
            <p>Phone: {app.phone}</p>
            <p>Source: {app.source}</p>
          </div>
        </section>

        {/* Application Details */}
        {(app.experience ||
          app.noticePeriod ||
          app.expectedCTC ||
          app.portfolioUrl ||
          app.linkedinUrl ||
          app.coverNote ||
          app.submittedAt) && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-on-surface">Application Details</h3>
            <div className="space-y-1 text-sm text-on-surface-variant">
              {app.experience && <p>Experience: {app.experience}</p>}
              {app.noticePeriod && <p>Notice Period: {app.noticePeriod}</p>}
              {app.expectedCTC && <p>Expected CTC: {app.expectedCTC}</p>}
              {app.portfolioUrl && (
                <p>
                  Portfolio:{' '}
                  <a
                    href={app.portfolioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {app.portfolioUrl}
                  </a>
                </p>
              )}
              {app.linkedinUrl && (
                <p>
                  LinkedIn:{' '}
                  <a
                    href={app.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {app.linkedinUrl}
                  </a>
                </p>
              )}
              {app.coverNote && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs font-medium text-on-surface">Cover Note</summary>
                  <p className="mt-1 whitespace-pre-wrap rounded-md bg-surface-variant p-2 text-xs">
                    {app.coverNote}
                  </p>
                </details>
              )}
              {app.submittedAt && (
                <p>
                  Submitted:{' '}
                  {new Date(app.submittedAt).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              )}
            </div>
          </section>
        )}

        {/* Stage Badge + Actions */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-on-surface">Current Stage</h3>
          <div className="flex items-center gap-3">
            <Badge tone={STAGE_TONES[app.stage]}>{STAGE_LABELS[app.stage]}</Badge>
            {canManage && app.stage !== 'hired' && app.stage !== 'rejected' && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => onAdvance(app)}>
                  <Check className="h-3 w-3" />
                  Advance
                </Button>
                <Button variant="danger" size="sm" onClick={() => onReject(app)}>
                  <X className="h-3 w-3" />
                  Reject
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Screening Results */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-on-surface">Screening Results</h3>
          {app.screeningScore != null ? (
            <div className="space-y-2">
              <p className="text-sm text-on-surface-variant">
                Score: <strong className="text-on-surface">{app.screeningScore}%</strong>
              </p>
              <div className="flex flex-wrap gap-1">
                {(job?.requiredSkills ?? []).map((skill) => {
                  const matched = (app.screeningMatchedSkills ?? []).includes(skill);
                  return (
                    <Badge key={skill} tone={matched ? 'success' : 'danger'} className="text-xs">
                      {matched ? '✓' : '✗'} {skill}
                    </Badge>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="text-sm text-on-surface-variant">Not screened yet.</p>
              {canManage && (
                <Button variant="ghost" size="sm" onClick={() => onScreen(app)}>
                  Run AI Screening
                </Button>
              )}
            </div>
          )}
        </section>

        {/* AI Remarks */}
        {app.aiScreeningRemarks && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-on-surface">AI Remarks</h3>
            <p className="rounded-md bg-surface-variant p-3 text-sm text-on-surface-variant">
              {app.aiScreeningRemarks}
            </p>
          </section>
        )}

        {/* Interview */}
        {app.interviewAt && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-on-surface">Interview</h3>
            <p className="text-sm text-on-surface-variant">
              Scheduled: {new Date(app.interviewAt).toLocaleString()}
            </p>
          </section>
        )}

        {/* Offer Letter */}
        {hasReachedOffer && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-on-surface">Offer Letter</h3>
            {linkedOffer ? (
              <div className="flex items-center justify-between rounded-md border border-outline p-3">
                <div>
                  <p className="text-sm font-medium text-on-surface">
                    {linkedOffer.designation || 'Designation pending'}
                  </p>
                  <Badge tone={OFFER_STATUS_TONES[linkedOffer.status]} className="mt-1">
                    {OFFER_STATUS_LABELS[linkedOffer.status]}
                  </Badge>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.offerLetters)}>
                  <FileText className="h-3.5 w-3.5" />
                  Open in Offer Letters
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <p className="text-sm text-on-surface-variant">Loading offer letter…</p>
            )}
          </section>
        )}

        {/* Rejection */}
        {app.stage === 'rejected' && app.rejectionReason && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-on-surface">Rejection Reason</h3>
            <p className="text-sm text-on-surface-variant">{app.rejectionReason}</p>
          </section>
        )}

        {/* Stage History */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-on-surface">Stage History</h3>
          {app.stageHistory.length === 0 ? (
            <p className="text-sm text-on-surface-variant">No stage transitions yet.</p>
          ) : (
            <ul className="space-y-2">
              {app.stageHistory.map((event, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <div>
                    <span className="text-on-surface">
                      {event.fromStage} → {STAGE_LABELS[event.toStage] ?? event.toStage}
                    </span>
                    {event.note && <p className="text-xs text-on-surface-variant">{event.note}</p>}
                    <p className="text-xs text-on-surface-variant">{timeAgo(event.occurredAt)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Resume */}
        {(app.resumeFile?.storageKey || app.resumeText) && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-on-surface">Resume</h3>
            {app.resumeFile?.storageKey && (
              <button
                type="button"
                onClick={() => openResume(app._id).catch((err) => toast.error(getApiErrorMessage(err)))}
                className="mb-3 inline-flex items-center gap-2 rounded-md border border-outline px-3 py-2 text-sm font-medium text-primary hover:bg-surface-variant"
              >
                <FileText className="h-3.5 w-3.5" />
                {app.resumeFile.originalName || 'View Resume (PDF)'}
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
            {app.resumeText && (
              <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md bg-surface-variant p-3 text-xs text-on-surface-variant">
                {app.resumeText}
              </pre>
            )}
          </section>
        )}
      </div>
    </Drawer>
  );
}

// ─── New Job Modal ──────────────────────────────────────────────────────────────

interface NewJobModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: {
    title: string;
    department: string;
    description: string;
    requiredSkills: string[];
    employmentType: EmploymentType;
    location: string;
    openings: number;
  }) => void;
  loading: boolean;
}

function NewJobModal({ open, onClose, onCreate, loading }: NewJobModalProps) {
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [description, setDescription] = useState('');
  const [skills, setSkills] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType>('full_time');
  const [location, setLocation] = useState('');
  const [openings, setOpenings] = useState('1');

  const { data: departments = [] } = useMasterDepartments();

  const handleSubmit = () => {
    if (!title.trim() || !department.trim()) return;
    onCreate({
      title: title.trim(),
      department: department.trim(),
      description: description.trim(),
      requiredSkills: skills
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      employmentType,
      location: location.trim(),
      openings: Math.max(1, parseInt(openings, 10) || 1),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Job Posting"
      size="lg"
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} disabled={!title.trim() || !department.trim()}>
            Create
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <Input
          label="Job Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Full Stack Developer"
        />
        <Select
          label="Department"
          options={departments.map((d) => ({ label: d.label, value: d.label }))}
          placeholder="Select a department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
        />
        <div>
          <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Description</label>
          <textarea
            className="h-24 w-full rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the role..."
          />
        </div>
        <Input
          label="Required Skills (comma separated)"
          value={skills}
          onChange={(e) => setSkills(e.target.value)}
          placeholder="React, TypeScript, Node.js"
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Employment Type"
            options={EMPLOYMENT_OPTIONS}
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}
          />
          <Input label="Location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Pune" />
        </div>
        <Input
          label="Openings"
          type="number"
          value={openings}
          onChange={(e) => setOpenings(e.target.value)}
          placeholder="1"
        />
      </div>
    </Modal>
  );
}

// ─── New Candidate Modal ────────────────────────────────────────────────────────

interface NewCandidateModalProps {
  open: boolean;
  onClose: () => void;
  jobs: JobPosting[];
  defaultJobId: string | null;
  onAdd: (payload: {
    jobId: string;
    candidateName: string;
    email: string;
    phone: string;
    source: string;
    coverNote: string;
    resume: File | null;
  }) => void;
  loading: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function NewCandidateModal({ open, onClose, jobs, defaultJobId, onAdd, loading }: NewCandidateModalProps) {
  const openJobs = useMemo(() => jobs.filter((j) => j.status === 'open'), [jobs]);
  const [jobId, setJobId] = useState<string>(() =>
    defaultJobId && openJobs.some((j) => j._id === defaultJobId) ? defaultJobId : openJobs[0]?._id ?? ''
  );
  const [candidateName, setCandidateName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState('direct');
  const [coverNote, setCoverNote] = useState('');
  const [resume, setResume] = useState<File | null>(null);
  const [emailError, setEmailError] = useState('');
  const [fileError, setFileError] = useState('');

  const noOpenJobs = openJobs.length === 0;

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setResume(null);
      setFileError('');
      return;
    }
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf) {
      setResume(null);
      setFileError('Only PDF resumes are accepted.');
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      setResume(null);
      setFileError('Resume must be 10 MB or smaller.');
      return;
    }
    setFileError('');
    setResume(file);
  }

  const handleSubmit = () => {
    if (noOpenJobs || !jobId || !candidateName.trim()) return;
    const trimmedEmail = email.trim();
    if (trimmedEmail && !EMAIL_RE.test(trimmedEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError('');
    onAdd({
      jobId,
      candidateName: candidateName.trim(),
      email: trimmedEmail,
      phone: phone.trim(),
      source,
      coverNote: coverNote.trim(),
      resume,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Candidate"
      size="md"
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} disabled={noOpenJobs || !jobId || !candidateName.trim()}>
            Add
          </Button>
        </div>
      }
    >
      {noOpenJobs ? (
        <div className="rounded-md border border-outline bg-surface-variant/40 p-4 text-sm text-on-surface-variant">
          There are no open job postings yet. Open a role first — candidates can only be added to a job that is
          accepting applications.
        </div>
      ) : (
        <div className="grid gap-4">
          <Select
            label="Job Role"
            options={openJobs.map((j) => ({
              label: j.location ? `${j.title} — ${j.location}` : j.title,
              value: j._id,
            }))}
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
          />
          <Input
            label="Candidate Name"
            value={candidateName}
            onChange={(e) => setCandidateName(e.target.value)}
            placeholder="John Doe"
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            placeholder="john@example.com"
            error={emailError || undefined}
          />
          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 (555) 000-0000"
          />
          <Select
            label="Source"
            options={[
              { label: 'Direct', value: 'direct' },
              { label: 'Referral', value: 'referral' },
              { label: 'LinkedIn', value: 'linkedin' },
              { label: 'Naukri', value: 'naukri' },
              { label: 'Job Board', value: 'job_board' },
              { label: 'Other', value: 'other' },
            ]}
            value={source}
            onChange={(e) => setSource(e.target.value)}
          />
          <div>
            <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Resume (PDF)</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="block w-full text-sm text-on-surface-variant file:mr-3 file:rounded-DEFAULT file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary/20"
            />
            {resume && !fileError && (
              <p className="mt-1.5 text-xs text-on-surface-variant">
                Selected: {resume.name} ({Math.round(resume.size / 1024)} KB)
              </p>
            )}
            {fileError && <p className="mt-1.5 text-xs text-danger">{fileError}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Cover Note (optional)</label>
            <textarea
              className="h-24 w-full rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
              value={coverNote}
              onChange={(e) => setCoverNote(e.target.value)}
              placeholder="Any notes about this candidate..."
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
